// RMA tab in ticket detail — lists RMA rows, send dialog, receive-back dialog.
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { repairApi, RMARequest, fmtDate } from "../repair-api";

const RMA_STATUS_LABELS: Record<string, string> = {
  sent: "Đã gửi",
  received_back: "Đã nhận lại",
  cancelled: "Huỷ",
};

const RMA_STATUS_COLORS: Record<string, string> = {
  sent: "bg-blue-100 text-blue-700",
  received_back: "bg-green-100 text-green-700",
  cancelled: "bg-gray-100 text-gray-500",
};

interface Props {
  ticketId: string;
  serialId?: string;
  rmas: RMARequest[];
}

export function RMASection({ ticketId, serialId, rmas }: Props) {
  const qc = useQueryClient();
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [receiveTarget, setReceiveTarget] = useState<string | null>(null);
  const [sendForm, setSendForm] = useState({ vendor: "", vendorRef: "", note: "" });
  const [receiveResult, setReceiveResult] = useState("");

  const sendRMA = useMutation({
    mutationFn: () =>
      repairApi.createRMA(ticketId, {
        serialId: serialId!,
        vendor: sendForm.vendor,
        vendorRef: sendForm.vendorRef || undefined,
        note: sendForm.note || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["repair-ticket", ticketId] });
      setShowSendDialog(false);
      setSendForm({ vendor: "", vendorRef: "", note: "" });
    },
  });

  const receiveBack = useMutation({
    mutationFn: (rmaId: string) => repairApi.receiveRMABack(rmaId, receiveResult),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["repair-ticket", ticketId] });
      setReceiveTarget(null);
      setReceiveResult("");
    },
  });

  if (!serialId) {
    return (
      <p className="text-sm text-muted-foreground py-6 text-center">
        Phiếu sửa chữa này không có serial — không thể tạo RMA.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {/* RMA list */}
      {rmas.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">Chưa có yêu cầu RMA</p>
      ) : (
        <div className="divide-y rounded-lg border">
          {rmas.map((r) => (
            <div key={r.id} className="p-4 space-y-1">
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm font-semibold">{r.code}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${RMA_STATUS_COLORS[r.status] ?? ""}`}>
                  {RMA_STATUS_LABELS[r.status] ?? r.status}
                </span>
              </div>
              <div className="text-sm text-muted-foreground">
                Hãng: <span className="text-foreground">{r.vendor}</span>
                {r.vendorRef && <> · Ref: <span className="text-foreground">{r.vendorRef}</span></>}
              </div>
              <div className="text-xs text-muted-foreground">Gửi lúc: {fmtDate(r.sentAt)}</div>
              {r.receivedBackAt && (
                <div className="text-xs text-muted-foreground">
                  Nhận lại: {fmtDate(r.receivedBackAt)}
                  {r.result && <> · Kết quả: <span className="text-foreground">{r.result}</span></>}
                </div>
              )}
              {r.note && <p className="text-xs text-muted-foreground">Ghi chú: {r.note}</p>}
              {r.status === "sent" && (
                <button
                  onClick={() => setReceiveTarget(r.id)}
                  className="mt-2 rounded-md border px-3 py-1 text-xs font-medium hover:bg-accent"
                >
                  Nhận máy về
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Send RMA button */}
      <button
        onClick={() => setShowSendDialog(true)}
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
      >
        Gửi RMA hãng
      </button>

      {/* Send RMA dialog */}
      {showSendDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-xl bg-card p-6 shadow-xl space-y-4">
            <h3 className="font-semibold text-lg">Gửi RMA hãng</h3>

            <label className="block space-y-1 text-sm">
              <span className="text-muted-foreground">Tên hãng <span className="text-destructive">*</span></span>
              <input
                type="text"
                className="w-full rounded-md border bg-background px-3 py-2 focus:outline-none"
                value={sendForm.vendor}
                onChange={(e) => setSendForm((f) => ({ ...f, vendor: e.target.value }))}
                placeholder="VD: Samsung, Apple, Asus..."
              />
            </label>
            <label className="block space-y-1 text-sm">
              <span className="text-muted-foreground">Mã tham chiếu hãng</span>
              <input
                type="text"
                className="w-full rounded-md border bg-background px-3 py-2 focus:outline-none"
                value={sendForm.vendorRef}
                onChange={(e) => setSendForm((f) => ({ ...f, vendorRef: e.target.value }))}
                placeholder="Tuỳ chọn"
              />
            </label>
            <label className="block space-y-1 text-sm">
              <span className="text-muted-foreground">Ghi chú</span>
              <textarea
                rows={2}
                className="w-full rounded-md border bg-background px-3 py-2 focus:outline-none"
                value={sendForm.note}
                onChange={(e) => setSendForm((f) => ({ ...f, note: e.target.value }))}
              />
            </label>

            {sendRMA.isError && <p className="text-sm text-destructive">Lỗi khi tạo RMA</p>}

            <div className="flex justify-end gap-3">
              <button onClick={() => setShowSendDialog(false)} className="rounded-md border px-4 py-2 text-sm">Huỷ</button>
              <button
                onClick={() => sendRMA.mutate()}
                disabled={!sendForm.vendor || sendRMA.isPending}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-40"
              >
                {sendRMA.isPending ? "Đang gửi..." : "Xác nhận gửi"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receive-back dialog */}
      {receiveTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-xl bg-card p-6 shadow-xl space-y-4">
            <h3 className="font-semibold text-lg">Nhận máy về từ hãng</h3>
            <label className="block space-y-1 text-sm">
              <span className="text-muted-foreground">Kết quả xử lý từ hãng</span>
              <textarea
                rows={3}
                className="w-full rounded-md border bg-background px-3 py-2 focus:outline-none"
                value={receiveResult}
                onChange={(e) => setReceiveResult(e.target.value)}
                placeholder="VD: Thay bo mạch chính, máy hoạt động bình thường..."
              />
            </label>
            {receiveBack.isError && <p className="text-sm text-destructive">Lỗi khi cập nhật</p>}
            <div className="flex justify-end gap-3">
              <button onClick={() => setReceiveTarget(null)} className="rounded-md border px-4 py-2 text-sm">Huỷ</button>
              <button
                onClick={() => receiveBack.mutate(receiveTarget)}
                disabled={receiveBack.isPending}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-40"
              >
                {receiveBack.isPending ? "Đang lưu..." : "Xác nhận nhận về"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
